import { useEffect, useRef, useState } from "react";
import { BOTFATHER_URL } from "../../../config/links";
import { getCsrfHeader } from "../../../lib/csrf";
import { extractBotToken, extractPairingCode } from "./telegramSetup";
import styles from "./TelegramSetupModal.module.css";

type Props = {
  open: boolean;
  // The tenant whose OpenClaw we wire Telegram onto.
  tenantId: string;
  // Integration title for copy (e.g. "Telegram").
  title: string;
  // Whether a bot is already wired onto this agent (connection 'pending' or
  // 'active'). When true we offer a skip-ahead path for pairing another
  // device; when false (a first-time setup) we hide it, since we now know
  // there's no bot to skip to.
  alreadyConnected?: boolean;
  onClose: () => void;
  // Called after the connection state changes (bot wired / paired) so the
  // detail page can refetch and reflect the new status.
  onChanged: () => void;
};

// Step model for the guided flow. Each step shows one focused action + a
// focused GIF of what to do. The two input steps (token, pair) call ztc-core.
type StepKey = "create" | "token" | "message" | "pair" | "done";

const STEPS: ReadonlyArray<{ key: StepKey; label: string }> = [
  { key: "create", label: "Create bot" },
  { key: "token", label: "Add token" },
  { key: "message", label: "Message bot" },
  { key: "pair", label: "Pair device" },
  { key: "done", label: "Done" },
];

// Per-step GIF + caption. The GIFs live in public/integrations/telegram/ and are
// served at /integrations/telegram/<file>. StepMedia falls back to a captioned
// placeholder if the asset is missing, so the flow always renders.
const STEP_MEDIA: Record<StepKey, { src: string | null; caption: string }> = {
  create: { src: "/integrations/telegram/step-create.gif", caption: "Opening @BotFather and running /newbot" },
  token: { src: "/integrations/telegram/step-token.gif", caption: "Copying the token BotFather sends you" },
  message: { src: "/integrations/telegram/step-message.gif", caption: "Messaging your bot to get a pairing code" },
  pair: { src: "/integrations/telegram/step-pair.gif", caption: "Copying the pairing code from your chat" },
  done: { src: "/integrations/telegram/step-done.gif", caption: "Your agent replying in Telegram" },
};

// A focused media slot for the current step. Shows the step's GIF when present,
// otherwise (or on load error) a captioned placeholder so the layout is stable
// whether or not the real assets are in place.
const StepMedia = ({ step }: { step: StepKey }): JSX.Element => {
  const media = STEP_MEDIA[step];
  const [errored, setErrored] = useState<boolean>(false);
  // Reset the error flag when the step changes so each step retries its own GIF.
  useEffect(() => {
    setErrored(false);
  }, [step]);
  return (
    <div className={styles.media}>
      {media.src && !errored ? (
        <img
          className={styles.mediaImg}
          src={media.src}
          alt={media.caption}
          onError={() => setErrored(true)}
        />
      ) : (
        <div className={styles.mediaPlaceholder}>
          <span className={styles.mediaTag}>GIF</span>
          <span className={styles.mediaCaption}>{media.caption}</span>
        </div>
      )}
    </div>
  );
};

// Guided, self-serve Telegram setup (setup_kind='guided'). A multi-step modal
// that walks the customer through creating their own bot via BotFather, pasting
// the token (which ztc-core pushes onto their OpenClaw), messaging the bot, and
// pasting the device-pairing code (which ztc-core approves). Native <dialog> +
// showModal() for focus-trap / Escape / backdrop, matching ApiKeyConnectModal.
export const TelegramSetupModal = ({
  open,
  tenantId,
  title,
  alreadyConnected = false,
  onClose,
  onChanged,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [botToken, setBotToken] = useState<string>("");
  const [pairingCode, setPairingCode] = useState<string>("");
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Soft "we don't see that code yet" hint on the pair step (distinct from a
  // hard error) so a too-early paste reads as "try again", not "broken".
  const [pairPending, setPairPending] = useState<boolean>(false);
  // Tracks whether the user reached the "message" step via the skip-ahead
  // shortcut (rather than the create -> token flow). Back on the message step
  // returns to "create" in that case, so a returning user isn't dropped onto
  // the token-entry form they deliberately skipped.
  const [skippedSetup, setSkippedSetup] = useState<boolean>(false);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setStepIndex(0);
      setBotToken("");
      setPairingCode("");
      setBotUsername(null);
      setSubmitting(false);
      setError(null);
      setPairPending(false);
      setSkippedSetup(false);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  if (!open) return null;

  const step: StepKey = STEPS[stepIndex]?.key ?? "create";

  // Paste-tolerant: pull the token/code out of whatever the customer pasted
  // (often BotFather's whole message). null = nothing valid yet.
  const tokenCandidate = extractBotToken(botToken);
  const codeCandidate = extractPairingCode(pairingCode);

  const close = (): void => {
    // Refetch on close so the page reflects any partial progress (e.g. the bot
    // was wired but pairing wasn't finished -> "pending").
    onChanged();
    onClose();
  };

  const submitBotToken = async (): Promise<void> => {
    const token = extractBotToken(botToken);
    if (token === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/telegram/bot-token`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ botToken: token }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Setup failed (${res.status})`);
      }
      const data = (await res.json()) as { botUsername: string | null };
      setBotUsername(data.botUsername);
      onChanged();
      setSkippedSetup(false);
      setStepIndex(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPairing = async (): Promise<void> => {
    const code = extractPairingCode(pairingCode);
    if (code === null) return;
    setSubmitting(true);
    setError(null);
    setPairPending(false);
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/telegram/pair`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ pairingCode: code }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Pairing failed (${res.status})`);
      }
      const data = (await res.json()) as { status: string };
      if (data.status === "approved") {
        onChanged();
        setStepIndex(4);
      } else {
        setPairPending(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const botLink: string | null =
    botUsername !== null && botUsername !== "" ? `https://t.me/${botUsername}` : null;
  const botHandle: string = botUsername !== null && botUsername !== "" ? `@${botUsername}` : "your bot";

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) close();
      }}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <h2 className={styles.title}>Set up {title}</h2>
          <ol className={styles.steps}>
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`${styles.stepDot} ${
                  i === stepIndex ? styles.stepDotActive : i < stepIndex ? styles.stepDotDone : ""
                }`}
              >
                <span className={styles.stepNum}>{i < stepIndex ? "✓" : i + 1}</span>
                <span className={styles.stepLabel}>{s.label}</span>
              </li>
            ))}
          </ol>
        </header>

        <div className={styles.split}>
          <div className={styles.body}>
          {step === "create" ? (
            <>
              <p className={styles.lead}>
                Telegram bots are created inside Telegram itself, by an official bot called <strong>BotFather</strong>. It takes about a minute.
              </p>
              <ol className={styles.stepList}>
                <li className={styles.stepItem}>
                  Open <strong>@BotFather</strong> in Telegram and start a chat.
                  <div className={styles.inlineAction}>
                    <a className={styles.actionBtn} href={BOTFATHER_URL} target="_blank" rel="noreferrer noopener">
                      Open @BotFather<span aria-hidden> →</span>
                    </a>
                  </div>
                </li>
                <li className={styles.stepItem}>
                  Send <code className={styles.code}>/newbot</code> and follow the prompts.
                </li>
                <li className={styles.stepItem}>
                  Name your bot and pick a username ending in <code className={styles.code}>bot</code>.
                </li>
                <li className={styles.stepItem}>
                  BotFather replies with a <strong>token</strong> like <code className={styles.code}>123456:ABC-DEF…</code>. Keep that chat open — you'll paste it next.
                </li>
              </ol>
              {alreadyConnected ? (
                <p className={styles.altPath}>
                  Your bot is already connected. Pairing another device? Skip ahead — message
                  the bot from that device and paste the code it replies with.
                </p>
              ) : null}
            </>
          ) : null}

          {step === "token" ? (
            <>
              <p className={styles.lead}>Paste the token BotFather sent you. We'll wire it onto your agent — this takes about a minute.</p>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Bot token</span>
                <input
                  className={styles.input}
                  type="text"
                  value={botToken}
                  onChange={(e) => {
                    setBotToken(e.target.value);
                    setError(null);
                  }}
                  placeholder="123456789:AA…"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={submitting}
                />
              </label>
              {submitting ? (
                <p className={styles.hint}>Connecting your bot… this can take up to a minute while your agent restarts.</p>
              ) : botToken.trim() !== "" && tokenCandidate === null ? (
                <p className={styles.hintWarn}>
                  That doesn't look like a bot token — it should look like{" "}
                  <code className={styles.code}>123456789:AA…</code>. You can paste the whole
                  BotFather message; we'll find the token.
                </p>
              ) : tokenCandidate !== null && tokenCandidate !== botToken.trim() ? (
                <p className={styles.hint}>Found your token in that message — we'll use it.</p>
              ) : null}
            </>
          ) : null}

          {step === "message" ? (
            <>
              <p className={styles.lead}>
                Your bot is connected{botUsername ? <> as <strong>{botHandle}</strong></> : null}. Now open it in Telegram and say hello so it can pair with you.
              </p>
              <ol className={styles.stepList}>
                <li className={styles.stepItem}>
                  Open {botHandle} in Telegram.
                  {botLink ? (
                    <div className={styles.inlineAction}>
                      <a className={styles.actionBtn} href={botLink} target="_blank" rel="noreferrer noopener">
                        Open {botHandle}<span aria-hidden> →</span>
                      </a>
                    </div>
                  ) : null}
                </li>
                <li className={styles.stepItem}>
                  Tap <strong>Start</strong> at the bottom if you see it — otherwise send it any message (even just "hi").
                </li>
                <li className={styles.stepItem}>
                  It replies with a <strong>pairing code</strong>. Copy that code — you'll paste it next.
                </li>
              </ol>
            </>
          ) : null}

          {step === "pair" ? (
            <>
              <p className={styles.lead}>Paste the pairing code {botHandle} sent you.</p>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Pairing code</span>
                <input
                  className={styles.input}
                  type="text"
                  value={pairingCode}
                  onChange={(e) => {
                    setPairingCode(e.target.value);
                    setError(null);
                    setPairPending(false);
                  }}
                  placeholder="e.g. 4F8K2P"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={submitting}
                />
              </label>
              {pairPending ? (
                <p className={styles.hint}>
                  We don't see that code yet. Make sure you messaged {botHandle} in Telegram, then try again.
                </p>
              ) : pairingCode.trim() !== "" && codeCandidate === null ? (
                <p className={styles.hintWarn}>
                  That doesn't look like a pairing code — it's a short code like{" "}
                  <code className={styles.code}>4FHTUTB8</code>. You can paste the bot's whole reply.
                </p>
              ) : null}
            </>
          ) : null}

          {step === "done" ? (
            <>
              <div className={styles.successMark} aria-hidden>✓</div>
              <p className={styles.lead}>
                <strong>You're connected.</strong> Send {botHandle} a message and your agent will reply right inside Telegram.
              </p>
              <p className={styles.hint}>You can add the bot to a group too — it'll respond there once paired.</p>
            </>
          ) : null}

            {error ? <div className={styles.errorBanner}>{error}</div> : null}
          </div>
          <div className={styles.mediaCol}>
            <StepMedia step={step} />
          </div>
        </div>

        <footer className={styles.footer}>
          {step === "create" ? (
            <>
              <button type="button" className={`${styles.secondaryBtn} ${styles.footerLead}`} onClick={close}>
                Cancel
              </button>
              {alreadyConnected ? (
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  data-testid="telegram-skip-ahead"
                  onClick={() => {
                    setSkippedSetup(true);
                    setStepIndex(2);
                  }}
                >
                  Already set up? Skip to pairing
                </button>
              ) : null}
              <button type="button" className={styles.primaryBtn} onClick={() => setStepIndex(1)}>
                I've created my bot
              </button>
            </>
          ) : null}

          {step === "token" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(0)} disabled={submitting}>
                Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void submitBotToken()}
                disabled={submitting || tokenCandidate === null}
              >
                {submitting ? "Connecting…" : "Connect bot"}
              </button>
            </>
          ) : null}

          {step === "message" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(skippedSetup ? 0 : 1)}>Back</button>
              <button type="button" className={styles.primaryBtn} onClick={() => setStepIndex(3)}>
                I have a pairing code
              </button>
            </>
          ) : null}

          {step === "pair" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(2)} disabled={submitting}>
                Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void submitPairing()}
                disabled={submitting || codeCandidate === null}
              >
                {submitting ? "Pairing…" : "Pair device"}
              </button>
            </>
          ) : null}

          {step === "done" ? (
            <button type="button" className={styles.primaryBtn} onClick={close}>Done</button>
          ) : null}
        </footer>
      </div>
    </dialog>
  );
};
